/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Rocket, Plus, Pencil, Trash2, X, Check, Clock,
    Sparkles, Wrench, Server, Tag, Send, Megaphone, Globe,
} from "lucide-react";

const CATEGORY_META = {
    major_feature: { label: "Major feature", icon: Rocket, color: "text-volt", pillBg: "bg-volt/15 border-volt/40 text-volt" },
    minor_feature: { label: "Minor feature", icon: Sparkles, color: "text-cyber", pillBg: "bg-cyber/15 border-cyber/40 text-cyber" },
    performance:   { label: "Performance",   icon: Wrench,  color: "text-neon",  pillBg: "bg-neon/15 border-neon/40 text-neon" },
    infra:         { label: "Infra",         icon: Server,  color: "text-zinc-400", pillBg: "bg-ink border-[#333] text-zinc-400" },
};

const STATUS_META = {
    shipped:     { label: "Shipped",     color: "text-volt",  pillBg: "bg-volt/15 border-volt/40 text-volt" },
    in_progress: { label: "In progress", color: "text-cyber", pillBg: "bg-cyber/15 border-cyber/40 text-cyber" },
    planned:     { label: "Planned",     color: "text-neon",  pillBg: "bg-neon/15 border-neon/40 text-neon" },
    backlog:     { label: "Backlog",     color: "text-zinc-400", pillBg: "bg-ink border-[#333] text-zinc-400" },
};

const AUDIENCE_OPTIONS = ["tenant_admin", "league_admin", "player", "platform_admin"];

const blankDraft = () => ({
    title: "",
    summary: "",
    body: "",
    category: "major_feature",
    status: "planned",
    audience: ["tenant_admin", "league_admin"],
    tags: "",
    shipped_at: "",
    planned_for: "",
});

export default function ReleaseNotesTab() {
    const [view, setView] = useState("shipped"); // shipped | roadmap
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // {id, ...} or null
    const [composing, setComposing] = useState(false);
    const [draft, setDraft] = useState(blankDraft());
    // iter76 — bulk forward
    const [selected, setSelected] = useState(() => new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [tenants, setTenants] = useState([]);
    const [selectedTenants, setSelectedTenants] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.listProductReleases();
            setReleases(data.releases || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load releases");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const shipped = releases.filter(r => r.status === "shipped");
    const roadmap = releases.filter(r => r.status !== "shipped");
    const visible = view === "shipped" ? shipped : roadmap;

    const beginCreate = () => {
        setDraft(blankDraft());
        setEditing(null);
        setComposing(true);
    };

    const beginEdit = (rel) => {
        setDraft({
            title: rel.title,
            summary: rel.summary,
            body: rel.body || "",
            category: rel.category,
            status: rel.status,
            audience: rel.audience || ["tenant_admin", "league_admin"],
            tags: (rel.tags || []).join(", "),
            shipped_at: rel.shipped_at || "",
            planned_for: rel.planned_for || "",
        });
        setEditing(rel);
        setComposing(true);
    };

    const cancelCompose = () => { setComposing(false); setEditing(null); setDraft(blankDraft()); };

    const submitDraft = async () => {
        if (!draft.title.trim() || !draft.summary.trim()) {
            toast.error("Title and summary are required");
            return;
        }
        const payload = {
            title: draft.title.trim(),
            summary: draft.summary.trim(),
            body: draft.body.trim() || null,
            category: draft.category,
            status: draft.status,
            audience: draft.audience,
            tags: draft.tags.split(",").map(t => t.trim()).filter(Boolean),
            shipped_at: draft.shipped_at || null,
            planned_for: draft.planned_for || null,
        };
        try {
            if (editing) {
                await api.updateProductRelease(editing.id, payload);
                toast.success("Release updated");
            } else {
                await api.createProductRelease(payload);
                toast.success("Release created");
            }
            cancelCompose();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const markShipped = async (rel) => {
        if (!window.confirm(`Mark "${rel.title}" as shipped? This publishes it to the public What's New feed.`)) return;
        try {
            await api.updateProductRelease(rel.id, { status: "shipped" });
            toast.success(`"${rel.title}" published`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mark-shipped failed");
        }
    };

    const remove = async (rel) => {
        if (!window.confirm(`Delete "${rel.title}"? This cannot be undone.`)) return;
        try {
            await api.deleteProductRelease(rel.id);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    // iter76 — bulk-forward helpers
    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const hasShippedSelected = Array.from(selected).some(id => {
        const r = releases.find(x => x.id === id);
        return r && r.status === "shipped";
    });

    const openBulk = async () => {
        setBulkOpen(true);
        try {
            const data = await api.listTenants();
            setTenants(data.tenants || data || []);
        } catch (e) {
            toast.error("Couldn't load tenants");
        }
    };

    const toggleTenant = (tid) => {
        setSelectedTenants(prev => {
            const next = new Set(prev);
            if (next.has(tid)) next.delete(tid); else next.add(tid);
            return next;
        });
    };

    const submitBulkForward = async () => {
        const shippedIds = Array.from(selected).filter(id => {
            const r = releases.find(x => x.id === id);
            return r && r.status === "shipped";
        });
        if (shippedIds.length === 0) {
            toast.error("Select at least one shipped release");
            return;
        }
        if (selectedTenants.size === 0) {
            toast.error("Select at least one tenant");
            return;
        }
        setBulkBusy(true);
        try {
            const r = await api.bulkForwardReleases(shippedIds, Array.from(selectedTenants), true);
            toast.success(`Pre-forwarded ${shippedIds.length} release(s) to ${selectedTenants.size} tenant(s) — ${r.applied} link(s) created.`);
            setBulkOpen(false);
            setSelected(new Set());
            setSelectedTenants(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk forward failed");
        }
        setBulkBusy(false);
    };

    const toggleAudience = (val) => {
        setDraft(d => {
            const next = new Set(d.audience);
            if (next.has(val)) next.delete(val); else next.add(val);
            return { ...d, audience: Array.from(next) };
        });
    };

    return (
        <div className="space-y-5" data-testid="release-notes-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                        <Megaphone className="w-6 h-6 text-volt" />
                        Release notes & roadmap
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1">
                        Curate the public "What's New" feed and the platform roadmap. Shipped items appear on
                        every Tenant Admin / Rivalry Admin dashboard. Planned items are platform-admin-only.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={beginCreate}
                    data-testid="release-notes-create-btn"
                    className="brutal-btn bg-volt text-white text-xs py-2 px-3"
                >
                    <Plus className="w-4 h-4" /> New release
                </button>
            </div>

            {/* iter76 — Bulk action bar shows up when items are selected */}
            {selected.size > 0 && (
                <div className="brutal-card-light p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="release-bulk-bar">
                    <p className="text-xs text-zinc-300">
                        <strong className="text-volt">{selected.size}</strong> release{selected.size === 1 ? "" : "s"} selected
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setSelected(new Set())}
                            className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                            data-testid="release-bulk-clear-btn"
                        >
                            Clear selection
                        </button>
                        <button
                            type="button"
                            onClick={openBulk}
                            disabled={!hasShippedSelected}
                            title={hasShippedSelected
                                ? "Pre-forward selected shipped releases to chosen tenants — they'll show as forwarded in each Tenant Admin's 'Platform Updates' tab. Tenant Admins can still untoggle."
                                : "Bulk-forward only applies to shipped releases. Select at least one shipped item."}
                            className="brutal-btn bg-cyber text-black text-xs py-2 px-3 disabled:opacity-30"
                            data-testid="release-bulk-forward-open-btn"
                        >
                            <Globe className="w-3.5 h-3.5" /> Pre-forward to tenants
                        </button>
                    </div>
                </div>
            )}

            {/* Sub-tabs */}
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setView("shipped")}
                    data-testid="release-notes-tab-shipped-btn"
                    className={`brutal-btn text-xs py-2 px-3 ${view === "shipped" ? "bg-volt text-white" : "bg-transparent text-white border-[#333] shadow-none"}`}
                >
                    <Rocket className="w-3.5 h-3.5" /> Shipped <span className="ml-1 opacity-60">({shipped.length})</span>
                </button>
                <button
                    type="button"
                    onClick={() => setView("roadmap")}
                    data-testid="release-notes-tab-roadmap-btn"
                    className={`brutal-btn text-xs py-2 px-3 ${view === "roadmap" ? "bg-volt text-white" : "bg-transparent text-white border-[#333] shadow-none"}`}
                >
                    <Clock className="w-3.5 h-3.5" /> Roadmap <span className="ml-1 opacity-60">({roadmap.length})</span>
                </button>
            </div>

            {composing && (
                <ComposeCard
                    editing={!!editing}
                    draft={draft}
                    onChange={(k, v) => setDraft(d => ({ ...d, [k]: v }))}
                    onToggleAudience={toggleAudience}
                    onCancel={cancelCompose}
                    onSubmit={submitDraft}
                />
            )}

            {loading && <p className="text-xs text-zinc-500">Loading…</p>}
            {!loading && visible.length === 0 && (
                <p className="text-sm text-zinc-500 italic">
                    Nothing {view === "shipped" ? "shipped yet" : "on the roadmap"}. Click "New release" to add one.
                </p>
            )}

            <ul className="space-y-3" data-testid="release-notes-list">
                {visible.map(rel => {
                    const cat = CATEGORY_META[rel.category] || CATEGORY_META.major_feature;
                    const stat = STATUS_META[rel.status] || STATUS_META.planned;
                    const Cat = cat.icon;
                    const isSelected = selected.has(rel.id);
                    return (
                        <li
                            key={rel.id}
                            data-testid={`release-row-${rel.id}`}
                            className={`brutal-card p-4 space-y-2 ${isSelected ? "ring-2 ring-volt" : ""}`}
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSelect(rel.id)}
                                    data-testid={`release-select-${rel.id}-cb`}
                                    className="mt-1.5"
                                    title={rel.status === "shipped"
                                        ? "Select for bulk pre-forward"
                                        : "Only shipped releases can be bulk-forwarded"}
                                />
                                <div className="flex-1 min-w-[260px]">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className={`pill text-[10px] font-bold ${cat.pillBg} flex items-center gap-1`}>
                                            <Cat className="w-3 h-3" /> {cat.label}
                                        </span>
                                        <span className={`pill text-[10px] font-bold ${stat.pillBg}`}>
                                            {stat.label}
                                        </span>
                                        {(rel.tags || []).map(t => (
                                            <span key={t} className="pill bg-ink border-[#333] text-[9px] text-zinc-400">
                                                <Tag className="w-2.5 h-2.5" /> {t}
                                            </span>
                                        ))}
                                    </div>
                                    <h3 className="font-display font-extrabold text-lg leading-tight">{rel.title}</h3>
                                    <p className="text-sm text-zinc-300 mt-1">{rel.summary}</p>
                                    {rel.body && (
                                        <p className="text-xs text-zinc-500 mt-2 whitespace-pre-wrap leading-relaxed">{rel.body}</p>
                                    )}
                                    <p className="text-[10px] text-zinc-600 mt-2">
                                        {rel.shipped_at && <>Shipped {new Date(rel.shipped_at).toLocaleDateString()} · </>}
                                        {rel.planned_for && <>Planned: {rel.planned_for} · </>}
                                        By {rel.author} · audience: {(rel.audience || []).join(", ")}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {rel.status !== "shipped" && (
                                        <button
                                            type="button"
                                            data-testid={`release-mark-shipped-${rel.id}-btn`}
                                            onClick={() => markShipped(rel)}
                                            className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                                            title="Promote to Shipped + publish to the public feed"
                                        >
                                            <Send className="w-3 h-3 text-volt" /> Ship
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`release-edit-${rel.id}-btn`}
                                        onClick={() => beginEdit(rel)}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                                    >
                                        <Pencil className="w-3 h-3" /> Edit
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`release-delete-${rel.id}-btn`}
                                        onClick={() => remove(rel)}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                                    >
                                        <Trash2 className="w-3 h-3 text-heat" />
                                    </button>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* iter76 — Bulk forward dialog */}
            {bulkOpen && (
                <div
                    className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
                    data-testid="release-bulk-dialog"
                    onClick={() => !bulkBusy && setBulkOpen(false)}
                >
                    <div
                        className="brutal-card max-w-lg w-full p-5 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="font-display font-extrabold uppercase text-lg flex items-center gap-2">
                                <Globe className="w-5 h-5 text-cyber" /> Pre-forward to tenants
                            </h3>
                            <button onClick={() => setBulkOpen(false)} disabled={bulkBusy} className="brutal-btn-ghost p-1">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-xs text-zinc-400 leading-relaxed">
                            Pre-forwards <strong className="text-volt">{Array.from(selected).filter(id => releases.find(r => r.id === id)?.status === "shipped").length}</strong> shipped release(s) to the tenants you pick below.
                            Each selected tenant's Tenant Admin will see these items marked <strong className="text-volt">Forwarded</strong> in their Platform Updates tab. They can still untoggle individually.
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {tenants.length === 0 && <p className="text-xs text-zinc-500 italic">Loading tenants…</p>}
                            <button
                                type="button"
                                onClick={() => setSelectedTenants(new Set(tenants.map(t => t.id)))}
                                className="brutal-btn-ghost text-[10px] py-1 px-2"
                                data-testid="release-bulk-select-all-tenants-btn"
                            >
                                Select all
                            </button>
                            {tenants.map(t => (
                                <label
                                    key={t.id}
                                    className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"
                                    data-testid={`release-bulk-tenant-${t.id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedTenants.has(t.id)}
                                        onChange={() => toggleTenant(t.id)}
                                    />
                                    <span className="font-mono text-xs">{t.name}</span>
                                    <span className="text-[10px] text-zinc-500">({t.id.slice(0, 8)}…)</span>
                                    {t.lifecycle_state && t.lifecycle_state !== "active" && (
                                        <span className="pill bg-heat/15 border-heat/40 text-heat text-[9px]">{t.lifecycle_state}</span>
                                    )}
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-[#222]">
                            <button
                                onClick={() => setBulkOpen(false)}
                                disabled={bulkBusy}
                                className="brutal-btn-ghost text-xs py-2 px-3"
                                data-testid="release-bulk-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitBulkForward}
                                disabled={bulkBusy || selectedTenants.size === 0}
                                className="brutal-btn bg-cyber text-black text-xs py-2 px-3 disabled:opacity-40"
                                data-testid="release-bulk-submit-btn"
                            >
                                {bulkBusy ? "Forwarding…" : `Forward to ${selectedTenants.size} tenant${selectedTenants.size === 1 ? "" : "s"}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function ComposeCard({ editing, draft, onChange, onToggleAudience, onCancel, onSubmit }) {
    return (
        <div className="brutal-card p-4 space-y-3" data-testid="release-compose-card">
            <div className="flex items-center justify-between">
                <h3 className="font-display font-extrabold uppercase">
                    {editing ? "Edit release" : "New release"}
                </h3>
                <button onClick={onCancel} className="brutal-btn-ghost text-xs p-1" data-testid="release-compose-close-btn">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Title">
                    <input
                        data-testid="release-input-title"
                        value={draft.title}
                        onChange={e => onChange("title", e.target.value)}
                        className="brutal-input w-full text-sm"
                        maxLength={140}
                        placeholder="What you'd put in a tweet"
                    />
                </Field>
                <Field label="Category">
                    <select
                        data-testid="release-input-category"
                        value={draft.category}
                        onChange={e => onChange("category", e.target.value)}
                        className="brutal-input w-full text-sm"
                    >
                        {Object.entries(CATEGORY_META).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Status">
                    <select
                        data-testid="release-input-status"
                        value={draft.status}
                        onChange={e => onChange("status", e.target.value)}
                        className="brutal-input w-full text-sm"
                    >
                        {Object.entries(STATUS_META).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </Field>
                <Field label={draft.status === "shipped" ? "Shipped on" : "Planned for"}>
                    {draft.status === "shipped" ? (
                        <input
                            data-testid="release-input-shipped-at"
                            type="date"
                            value={draft.shipped_at ? draft.shipped_at.slice(0, 10) : ""}
                            onChange={e => onChange("shipped_at", e.target.value)}
                            className="brutal-input w-full text-sm"
                        />
                    ) : (
                        <input
                            data-testid="release-input-planned-for"
                            value={draft.planned_for}
                            onChange={e => onChange("planned_for", e.target.value)}
                            className="brutal-input w-full text-sm"
                            placeholder='e.g. "Q2 2026" or "v1.5"'
                        />
                    )}
                </Field>
            </div>
            <Field label="Summary (1–2 lines)">
                <textarea
                    data-testid="release-input-summary"
                    value={draft.summary}
                    onChange={e => onChange("summary", e.target.value)}
                    className="brutal-input w-full text-sm"
                    maxLength={300}
                    rows={2}
                    placeholder="What the user can now do that they couldn't before"
                />
            </Field>
            <Field label="Body (optional, longer detail)">
                <textarea
                    data-testid="release-input-body"
                    value={draft.body}
                    onChange={e => onChange("body", e.target.value)}
                    className="brutal-input w-full text-sm font-mono"
                    maxLength={4000}
                    rows={4}
                    placeholder="Bullet points, screenshots later, etc. Plain text for now."
                />
            </Field>
            <Field label="Tags (comma-separated)">
                <input
                    data-testid="release-input-tags"
                    value={draft.tags}
                    onChange={e => onChange("tags", e.target.value)}
                    className="brutal-input w-full text-sm"
                    placeholder="e.g. draft, messaging, subscriptions"
                />
            </Field>
            <Field label="Audience">
                <p className="text-[10px] text-zinc-500 -mt-1 mb-2 leading-relaxed">
                    Which roles will see this in their What's New modal (assuming their tenant admin has forwarded it).
                    Toggle on the roles you want this entry to be visible to. <strong className="text-zinc-300">Saving doesn't auto-publish</strong> —
                    set status to <strong className="text-volt">Shipped</strong> to publish to the public feed.
                </p>
                <div className="flex flex-wrap gap-2">
                    {AUDIENCE_OPTIONS.map(a => {
                        const on = draft.audience.includes(a);
                        return (
                            <button
                                key={a}
                                type="button"
                                onClick={() => onToggleAudience(a)}
                                data-testid={`release-audience-${a}-btn`}
                                className={`brutal-btn text-[10px] py-1.5 px-2 ${on ? "bg-cyber text-black" : "bg-transparent text-zinc-400 border-[#333] shadow-none"}`}
                            >
                                {on && <Check className="w-3 h-3" />} {a.replace("_", " ")}
                            </button>
                        );
                    })}
                </div>
            </Field>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#222]">
                <button onClick={onCancel} className="brutal-btn-ghost text-xs py-2 px-3" data-testid="release-compose-cancel-btn">Cancel</button>
                <button onClick={onSubmit} className="brutal-btn bg-volt text-white text-xs py-2 px-3" data-testid="release-compose-submit-btn">
                    {editing ? "Save changes" : "Create"}
                </button>
            </div>
        </div>
    );
}


function Field({ label, children }) {
    return (
        <div>
            <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">{label}</p>
            {children}
        </div>
    );
}
